import Phaser from 'phaser';
import { COLORS } from '../colors';
import { GAME_WIDTH } from '../grid';
import { VERSION_LABEL } from '../version';

export type FluidsState = 'idle' | 'flowing';

/**
 * One entry in the temporary debug level-select row. `step` is the displayed
 * label number (so a placeholder button can show e.g. "L8" without that step
 * existing yet). `levelIndex` is the index into `LEVELS` to navigate to on
 * click — `null` means placeholder (button is dimmed and inert).
 */
export interface LevelSelectEntry {
  step: number;
  levelIndex: number | null;
}

export interface TopBarCallbacks {
  onClear: () => void;
  onToggleFluids: () => void;
  onBack: () => void;
  onNext: () => void;
  onLevelSelect: (index: number) => void;
  levels: LevelSelectEntry[];
}

/**
 * Top UI bar: back arrow, sound toggle (cosmetic), CLEAR PIPES, START/STOP FLUIDS,
 * and a NEXT STEP button that appears when the current level is solved.
 */
export class TopBar {
  private fluidsState: FluidsState = 'idle';

  private fluidsButtonBg: Phaser.GameObjects.Graphics;
  private fluidsButtonText: Phaser.GameObjects.Text;
  private fluidsButtonHit: Phaser.GameObjects.Zone;

  private nextButtonContainer: Phaser.GameObjects.Container;
  private backArrowAlpha: Phaser.GameObjects.Container;
  private backEnabled = true;
  private levelButtons: Array<{
    bg: Phaser.GameObjects.Graphics;
    text: Phaser.GameObjects.Text;
    /** Index into LEVELS, or null for a placeholder button (no level wired). */
    levelIndex: number | null;
    /** Hit zone — absent for placeholders, which are inert. */
    hit?: Phaser.GameObjects.Zone;
  }> = [];

  constructor(scene: Phaser.Scene, cb: TopBarCallbacks) {
    // Background plate
    const bg = scene.add.graphics();
    bg.fillStyle(COLORS.uiBarFill, 1);
    bg.fillRect(0, 0, GAME_WIDTH, 80);
    bg.lineStyle(2, COLORS.uiBarStroke, 1);
    bg.strokeLineShape(new Phaser.Geom.Line(0, 80, GAME_WIDTH, 80));

    // Back arrow (now functional)
    this.backArrowAlpha = this.drawIconButton(scene, 36, 40, '←');
    const backHit = scene.add
      .zone(36, 40, 44, 36)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    backHit.on('pointerdown', () => {
      if (this.backEnabled) cb.onBack();
    });

    // Sound toggle (cosmetic for v1)
    this.drawIconButton(scene, 96, 40, '♪');

    // Level-select row. Hidden during normal play (students progress linearly
    // via NEXT STEP / the back arrow); revealed permanently once the game is
    // beaten so finished students can replay any level. See GameScene's win
    // handling and `setLevelSelectVisible`.
    this.buildLevelSelectRow(scene, cb.levels, cb.onLevelSelect);
    this.setLevelSelectVisible(false);

    // CLEAR PIPES button
    const clearX = GAME_WIDTH - 320;
    const { hit: clearHit } = this.drawTextButton(
      scene,
      clearX,
      40,
      'CLEAR PIPES',
      COLORS.buttonIdleFill,
      COLORS.buttonIdleText,
      170,
      48,
    );
    clearHit.on('pointerdown', () => cb.onClear());

    // START/STOP FLUIDS button
    const fluidsX = GAME_WIDTH - 130;
    const fluidsBtn = this.drawTextButton(
      scene,
      fluidsX,
      40,
      'START FLUIDS',
      COLORS.buttonStartFill,
      COLORS.buttonStartText,
      180,
      48,
    );
    this.fluidsButtonBg = fluidsBtn.bg;
    this.fluidsButtonText = fluidsBtn.text;
    this.fluidsButtonHit = fluidsBtn.hit;
    this.fluidsButtonHit.on('pointerdown', () => cb.onToggleFluids());

    // Version label, tucked in the top-right corner of the bar.
    // Lets us confirm at a glance whether iPad Safari is on the latest Netlify
    // deploy. SHA half auto-bumps on every commit so a stale cache is obvious.
    scene.add
      .text(GAME_WIDTH - 6, 4, VERSION_LABEL, {
        fontFamily: 'Menlo, Consolas, monospace',
        fontSize: '11px',
        color: '#888888',
      })
      .setOrigin(1, 0);

    // NEXT STEP button — hidden by default, shown on level success.
    // The hit zone stays interactive at all times but ignores clicks while the visual is hidden
    // (clicks are gated by the container's `visible` flag inside the handler).
    const built = this.buildNextStepButton(scene, () => {
      if (this.nextButtonContainer.visible) cb.onNext();
    });
    this.nextButtonContainer = built.container;
    this.nextButtonContainer.setVisible(false);
  }

  setFluidsState(state: FluidsState): void {
    this.fluidsState = state;
    if (state === 'flowing') {
      this.redrawFluidsButton(180, 48, COLORS.buttonStopFill, 'STOP FLUIDS', COLORS.buttonStopText);
    } else {
      this.redrawFluidsButton(
        180,
        48,
        COLORS.buttonStartFill,
        'START FLUIDS',
        COLORS.buttonStartText,
      );
    }
  }

  getFluidsState(): FluidsState {
    return this.fluidsState;
  }

  setNextVisible(visible: boolean): void {
    this.nextButtonContainer.setVisible(visible);
  }

  setBackEnabled(enabled: boolean): void {
    this.backEnabled = enabled;
    this.backArrowAlpha.setAlpha(enabled ? 1 : 0.35);
  }

  setCurrentLevelHighlight(activeIndex: number): void {
    this.levelButtons.forEach((btn) => {
      const active = btn.levelIndex === activeIndex;
      const isPlaceholder = btn.levelIndex === null;
      this.paintLevelButton(btn.bg, 50, 36, active ? COLORS.buttonStartFill : 0xeaeaea);
      btn.text.setColor(active ? this.hexColor(COLORS.buttonStartText) : '#222222');
      // Placeholders stay dimmed regardless of active state (can't be active anyway).
      const alpha = isPlaceholder ? 0.4 : 1;
      btn.bg.setAlpha(alpha);
      btn.text.setAlpha(alpha);
    });
  }

  /**
   * Show or hide the entire level-select row. Hidden by default so the student
   * build presents a clean linear progression; GameScene reveals it permanently
   * once the final level is beaten (persisted), letting finished students jump
   * back to replay any level. Toggles both the visuals and the hit zones, so
   * hidden buttons can't be clicked.
   */
  setLevelSelectVisible(visible: boolean): void {
    this.levelButtons.forEach((btn) => {
      btn.bg.setVisible(visible);
      btn.text.setVisible(visible);
      if (btn.hit) {
        if (visible) btn.hit.setInteractive({ useHandCursor: true });
        else btn.hit.disableInteractive();
      }
    });
  }

  private buildLevelSelectRow(
    scene: Phaser.Scene,
    levels: LevelSelectEntry[],
    onSelect: (i: number) => void,
  ): void {
    const btnW = 50;
    const btnH = 36;
    const gap = 6;
    const startX = 150; // just right of the sound toggle
    const cy = 40;

    levels.forEach((entry, i) => {
      const cx = startX + i * (btnW + gap) + btnW / 2;
      const bg = scene.add.graphics();
      this.paintLevelButton(bg, btnW, btnH, 0xeaeaea);
      bg.setPosition(cx, cy);

      const text = scene.add
        .text(cx, cy, `L${entry.step}`, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '16px',
          color: '#222222',
        })
        .setOrigin(0.5, 0.5);

      const isPlaceholder = entry.levelIndex === null;
      let hit: Phaser.GameObjects.Zone | undefined;
      if (isPlaceholder) {
        // Dim the placeholder visually; no hit zone, so it's inert.
        bg.setAlpha(0.4);
        text.setAlpha(0.4);
      } else {
        hit = scene.add
          .zone(cx, cy, btnW, btnH)
          .setOrigin(0.5, 0.5)
          .setInteractive({ useHandCursor: true });
        const targetIdx = entry.levelIndex!;
        hit.on('pointerdown', () => onSelect(targetIdx));
      }

      this.levelButtons.push({ bg, text, levelIndex: entry.levelIndex, hit });
    });
  }

  private paintLevelButton(
    g: Phaser.GameObjects.Graphics,
    w: number,
    h: number,
    fill: number,
  ): void {
    g.clear();
    g.fillStyle(0x000000, 0.35);
    g.fillRoundedRect(-w / 2 + 2, -h / 2 + 2, w, h, 6);
    g.fillStyle(fill, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    g.lineStyle(2, 0x000000, 0.3);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
  }

  private buildNextStepButton(
    scene: Phaser.Scene,
    onClick: () => void,
  ): { container: Phaser.GameObjects.Container; hit: Phaser.GameObjects.Zone } {
    const w = 220;
    const h = 64;
    // Center above the clipboard, just below the workspace.
    const x = GAME_WIDTH / 2;
    const y = 760;

    const container = scene.add.container(x, y);
    container.setDepth(60);

    const bg = scene.add.graphics();
    bg.fillStyle(0x000000, 0.4);
    bg.fillRoundedRect(-w / 2 + 3, -h / 2 + 4, w, h, 10);
    bg.fillStyle(COLORS.buttonStartFill, 1);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    bg.lineStyle(3, 0x000000, 0.4);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
    container.add(bg);

    const text = scene.add
      .text(0, 0, 'NEXT STEP →', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '26px',
        color: this.hexColor(COLORS.buttonStartText),
      })
      .setOrigin(0.5, 0.5);
    container.add(text);

    // Hit zone is independent of the container (same pattern as CLEAR PIPES) — at world coords.
    const hit = scene.add
      .zone(x, y, w, h)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    hit.setDepth(61);
    hit.on('pointerdown', () => onClick());

    return { container, hit };
  }

  private redrawFluidsButton(
    w: number,
    h: number,
    fill: number,
    label: string,
    textColor: number,
  ): void {
    this.fluidsButtonBg.clear();
    this.fluidsButtonBg.fillStyle(0x000000, 0.4);
    this.fluidsButtonBg.fillRoundedRect(-w / 2 + 2, -h / 2 + 3, w, h, 8);
    this.fluidsButtonBg.fillStyle(fill, 1);
    this.fluidsButtonBg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    this.fluidsButtonBg.lineStyle(2, 0x000000, 0.4);
    this.fluidsButtonBg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    this.fluidsButtonText.setText(label);
    this.fluidsButtonText.setColor(this.hexColor(textColor));
  }

  private drawTextButton(
    scene: Phaser.Scene,
    x: number,
    y: number,
    label: string,
    fill: number,
    textColor: number,
    w: number,
    h: number,
  ): {
    bg: Phaser.GameObjects.Graphics;
    text: Phaser.GameObjects.Text;
    hit: Phaser.GameObjects.Zone;
  } {
    const container = scene.add.container(x, y);
    const bg = scene.add.graphics();
    bg.fillStyle(0x000000, 0.4);
    bg.fillRoundedRect(-w / 2 + 2, -h / 2 + 3, w, h, 8);
    bg.fillStyle(fill, 1);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(2, 0x000000, 0.4);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    container.add(bg);

    const text = scene.add.text(0, 0, label, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '20px',
      color: this.hexColor(textColor),
    });
    text.setOrigin(0.5, 0.5);
    container.add(text);

    const hit = scene.add.zone(x, y, w, h);
    hit.setOrigin(0.5, 0.5);
    hit.setInteractive({ useHandCursor: true });
    return { bg, text, hit };
  }

  private drawIconButton(
    scene: Phaser.Scene,
    x: number,
    y: number,
    glyph: string,
  ): Phaser.GameObjects.Container {
    const container = scene.add.container(x, y);
    const g = scene.add.graphics();
    g.fillStyle(0xeaeaea, 1);
    g.fillRoundedRect(-22, -18, 44, 36, 6);
    g.lineStyle(2, 0x222222, 0.6);
    g.strokeRoundedRect(-22, -18, 44, 36, 6);
    container.add(g);
    container.add(
      scene.add
        .text(0, 0, glyph, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '22px',
          color: '#222222',
        })
        .setOrigin(0.5, 0.5),
    );
    return container;
  }

  private hexColor(n: number): string {
    return '#' + n.toString(16).padStart(6, '0');
  }
}
